import React, { useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { testIntegrationAuth } from "../../services/RepositoryService";

/**
 * "Test Auth" button + inline result panel, used on the Authentication tab.
 *
 * `testPayload` is `{ id, integration_key, config, ... }` — works both for a
 * saved integration (has `id`, backend loads the persisted row) and for an
 * unsaved draft (no `id`, backend uses `integration_key`/`config` directly)
 * so you can verify auth before ever clicking Save.
 *
 * `unsupported`: true when the currently-selected auth scheme has no real
 * request-signing logic yet (currently only oauth2_authorization_code) —
 * shows a disabled "Connect" stub instead of a live Test Auth button.
 */
const IntegrationTestAuthPanel = ({ testPayload, schemeName, unsupported }) => {
    const [testing, setTesting] = useState(false);
    const [result, setResult] = useState(null);

    const canTest = !!(testPayload?.id || testPayload?.integration_key);

    const runTestAuth = async () => {
        setTesting(true);
        setResult(null);
        try {
            const res = await testIntegrationAuth(testPayload);
            setResult(res);
        } catch (err) {
            setResult({ success: false, message: err?.message || "Request failed" });
        } finally {
            setTesting(false);
        }
    };

    if (unsupported) {
        return (
            <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                disabled
                title={`${schemeName || "This auth scheme"} isn't supported yet`}
            >
                <i className="fa fa-lock me-1" />
                Connect via {schemeName || "provider"} (Coming soon)
            </button>
        );
    }

    return (
        <div>
            <div className="d-flex align-items-center gap-2 mb-2">
                <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    disabled={!canTest || testing}
                    title={canTest ? "Verify authentication" : "Select an integration template first"}
                    onClick={runTestAuth}
                >
                    {testing ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" className="me-1" />
                            Testing…
                        </>
                    ) : (
                        <><i className="fa fa-shield me-1" />Test Auth</>
                    )}
                </button>
            </div>

            {result && (
                <Alert variant={result.success ? "success" : "danger"} className="py-2 px-3 small mb-0">
                    <i className={`fa ${result.success ? "fa-check-circle" : "fa-exclamation-triangle"} me-2`} />
                    {result.message}
                </Alert>
            )}
        </div>
    );
};

export default IntegrationTestAuthPanel;
